import { Button } from "@/components/ui/button";
import { 
  PlusIcon, 
  PencilIcon, 
  CalendarDaysIcon, 
  Sparkles, 
  Zap,
  Users,
  ChevronDown,
  UserIcon,
  Settings
} from "lucide-react";
import { useLocation } from "wouter";
import { useParams } from "wouter";
import { formatDateRange } from "@/lib/utils";
import { addDays } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import AIPlannerSheet from "@/components/AIPlannerSheet";
import QuickAIPlanner from "@/components/QuickAIPlanner";
import { Client, ClientTrip, type TripPaymentStatus } from "@shared/schema";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useState } from "react";
import { TripPaymentStatusBadge } from "@/components/ui/TripPaymentStatusBadge";

interface HeaderProps {
  tripName: string;
  onEditTrip?: () => void;
  dateRange?: [Date, Date];
  tripId?: number;
  recordLocator?: string | null;
  contacts?: Client[];
  contactTrips?: ClientTrip[];
  tripStatus?: TripPaymentStatus | null;
  hasDerivedDates?: boolean;
  travelers?: string[];
}

export default function Header({ tripName, onEditTrip, dateRange, tripId, recordLocator, contacts, contactTrips, tripStatus, hasDerivedDates, travelers }: HeaderProps) {
  const [, navigate] = useLocation();
  const params = useParams();
  const isInTripDetail = !!params.id;
  const [aiPlannerOpen, setAiPlannerOpen] = useState(false);
  const { toast } = useToast();

  const handleSeedSampleData = async () => {
    try {
      if (!tripId || !dateRange) return;
      const start = new Date(dateRange[0]);
      const day1 = addDays(start, 1);

      // Create a couple of day destinations
      await fetch(`/api/trips/${tripId}/day-destinations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tripId, dayIndex: 0, destination: "Milan, Italy", notes: null })
      });
      await fetch(`/api/trips/${tripId}/day-destinations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tripId, dayIndex: 1, destination: "Rome, Italy", notes: null })
      });

      // Create a few components
      const components = [
        {
          tripId,
          type: "activity",
          title: "Milan Cathedral (Duomo) guided visit",
          description: "Morning tour of the Duomo and rooftop terrace",
          startDate: start,
          endDate: null,
          location: "Milan, Italy",
          dayIndex: 0,
          startTime: "09:00",
          endTime: "11:00",
          details: { category: "culture" }
        },
        {
          tripId,
          type: "food",
          title: "Lunch at Galleria Vittorio Emanuele II",
          description: "Casual lunch near the Duomo",
          startDate: start,
          endDate: null,
          location: "Milan, Italy",
          dayIndex: 0,
          startTime: "12:30",
          endTime: "13:30",
          details: { reservation: false }
        },
        {
          tripId,
          type: "transport",
          title: "Frecciarossa high‑speed train to Rome",
          description: "Milan Centrale → Roma Termini",
          startDate: day1,
          endDate: null,
          location: "Italy",
          dayIndex: 1,
          startTime: "10:15",
          endTime: "13:30",
          details: { class: "Business" }
        }
      ];

      await Promise.all(
        components.map((c) =>
          fetch("/api/components", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(c)
          })
        )
      );

      // Refresh queries
      queryClient.invalidateQueries({ queryKey: [`/api/trips/${tripId}/components`] });
      queryClient.invalidateQueries({ queryKey: [`/api/trips/${tripId}/day-destinations`] });

      toast({ title: "Sample data added", description: "A couple of items and destinations were created." });
    } catch (err) {
      toast({ title: "Failed to add sample data", description: "Please try again.", variant: "destructive" });
    }
  };

  const handleNewTrip = () => {
    navigate("/");
  };

  return (
    <header className="bg-white dark:bg-gray-800 shadow-sm">
      <div className="flex px-[50px] py-4 justify-between items-center w-full">
        <div className="flex items-center space-x-4">
          {isInTripDetail ? (
            <Button
              variant="ghost"
              className="flex items-center space-x-2"
              onClick={() => navigate("/")}
            >
              <span>← Back to Trips</span>
            </Button>
          ) : (
            <h1
              className="text-2xl font-bold text-primary cursor-pointer"
              onClick={() => navigate("/")}
            >
              AIVIA
            </h1>
          )}
          <div className="hidden md:flex items-center text-textColor">
            <div className="flex items-center">
              <span className="font-bold">{tripName}</span>
              {recordLocator && (
                <span className="ml-2 text-xs font-mono text-muted-foreground bg-muted px-1.5 py-0.5 rounded" title="Trip ID (Trip #)">
                  {recordLocator}
                </span>
              )}
              
              {dateRange && (
                <div className="flex items-center ml-2 text-gray-600" title={hasDerivedDates ? "Dates derived from linked invoices (min departure / max return)" : undefined}>
                  <CalendarDaysIcon className="h-4 w-4 mr-1" />
                  <span>{formatDateRange(dateRange)}</span>
                  {hasDerivedDates && (
                    <span className="ml-1 text-xs text-muted-foreground">(from invoices)</span>
                  )}
                </div>
              )}
              
              {tripStatus && (
                <div className="ml-3">
                  <TripPaymentStatusBadge status={tripStatus} />
                </div>
              )}
              
              {travelers && travelers.length > 0 && (
                <div className="flex items-center ml-3 text-gray-600">
                  <Users className="h-4 w-4 mr-1" />
                  <span className="text-sm">
                    {travelers[0]}{travelers.length > 1 ? ` +${travelers.length - 1}` : ''}
                  </span>
                </div>
              )}
              
              {/* Contact Information */}
              {contacts && contacts.length > 0 && (
                <div className="flex items-center ml-4 text-gray-600">
                  <div className="flex items-center gap-2">
                    <div className="flex -space-x-1">
                      {(() => {
                        // Sort contacts to show primary contact first
                        const sortedContacts = [...contacts].sort((a, b) => {
                          const aTrip = contactTrips?.find(ct => ct.clientId === a.id);
                          const bTrip = contactTrips?.find(ct => ct.clientId === b.id);
                          const aIsPrimary = aTrip?.role === 'primary';
                          const bIsPrimary = bTrip?.role === 'primary';
                          
                          if (aIsPrimary && !bIsPrimary) return -1;
                          if (!aIsPrimary && bIsPrimary) return 1;
                          return 0;
                        });
                        
                        return sortedContacts.slice(0, 2).map((contact) => (
                          <Avatar key={contact.id} className="h-6 w-6 border-2 border-white">
                            <AvatarFallback className="bg-cyan-100 text-cyan-800 text-xs">
                              {contact.firstName.charAt(0)}{contact.lastName.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                        ));
                      })()}
                      {contacts.length > 2 && (
                        <div className="h-6 w-6 rounded-full bg-gray-200 flex items-center justify-center text-xs border-2 border-white">
                          +{contacts.length - 2}
                        </div>
                      )}
                    </div>
                    <span className="text-sm">
                      {(() => {
                        // Sort contacts to show primary contact first
                        const sortedContacts = [...contacts].sort((a, b) => {
                          const aTrip = contactTrips?.find(ct => ct.clientId === a.id);
                          const bTrip = contactTrips?.find(ct => ct.clientId === b.id);
                          const aIsPrimary = aTrip?.role === 'primary';
                          const bIsPrimary = bTrip?.role === 'primary';
                          
                          if (aIsPrimary && !bIsPrimary) return -1;
                          if (!aIsPrimary && bIsPrimary) return 1;
                          return 0;
                        });
                        
                        const primaryContact = sortedContacts[0];
                        const primaryTrip = contactTrips?.find(ct => ct.clientId === primaryContact.id);
                        const isPrimary = primaryTrip?.role === 'primary';
                        
                        return `${primaryContact.firstName} ${primaryContact.lastName}${isPrimary ? ' (Primary)' : ''}${contacts.length > 1 ? ` +${contacts.length - 1} other${contacts.length > 2 ? 's' : ''}` : ''}`;
                      })()}
                    </span>
                  </div>
                </div>
              )}
              
              {isInTripDetail && onEditTrip && (
                <Button 
                  variant="ghost" 
                  size="icon" aria-label="Edit trip" 
                  className="ml-2 h-8 w-8 text-gray-500 hover:text-blue-500" 
                  onClick={onEditTrip}
                >
                  <PencilIcon className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Profile Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Open profile menu" className="relative">
                <UserIcon className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem 
                className="flex items-center gap-2 cursor-pointer"
                onClick={() => navigate("/profile")}
              >
                <UserIcon className="h-4 w-4" />
                <span>My Profile</span>
              </DropdownMenuItem>
              <DropdownMenuItem 
                className="flex items-center gap-2 cursor-pointer"
                onClick={() => navigate("/settings")}
              >
                <Settings className="h-4 w-4" />
                <span>Settings</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Unified AI Planning dropdown - only when on trip detail page */}
          {isInTripDetail && tripId && dateRange && (
            <>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost"
                    className="flex items-center gap-1 mr-3"
                  >
                    <Sparkles className="h-4 w-4 text-primary" />
                    <span>AI Planner</span>
                    <ChevronDown className="h-3 w-3 text-gray-500" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem 
                    className="flex items-center gap-2 cursor-pointer"
                    onClick={() => {
                      // Trigger QuickAIPlanner functionality
                      const quickPlannerElement = document.querySelector('[data-quick-ai-planner]') as HTMLButtonElement;
                      if (quickPlannerElement) {
                        quickPlannerElement.click();
                      }
                    }}
                  >
                    <Zap className="h-4 w-4 text-amber-500" />
                    <div className="flex flex-col">
                      <span className="font-medium">Quick Generate</span>
                      <span className="text-xs text-muted-foreground">Auto-generate and save instantly</span>
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    className="flex items-center gap-2 cursor-pointer"
                    onClick={() => setAiPlannerOpen(true)}
                  >
                    <Sparkles className="h-4 w-4 text-primary" />
                    <div className="flex flex-col">
                      <span className="font-medium">Advanced Planner</span>
                      <span className="text-xs text-muted-foreground">Preview, edit, and refine before saving</span>
                    </div>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Quick seed for demo/debug */}
              <Button variant="outline" className="text-xs" onClick={handleSeedSampleData}>
                Add sample data
              </Button>

              {/* Hidden QuickAIPlanner component for functionality */}
              <div className="hidden">
                <QuickAIPlanner
                  tripId={tripId}
                  tripName={tripName}
                  dateRange={dateRange}
                />
              </div>
              
              {/* AIPlannerSheet with controlled open state */}
              <AIPlannerSheet
                tripId={tripId}
                dateRange={dateRange}
                tripName={tripName}
                onGenerateSuccess={() => {}}
                open={aiPlannerOpen}
                onOpenChange={setAiPlannerOpen}
              />
            </>
          )}
          
          {!isInTripDetail && (
            <Button 
              onClick={handleNewTrip}
              className="flex items-center space-x-2"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              <span>New Trip</span>
            </Button>
          )}
        </div>
      </div>
    </header>
  );
}
